import type { HomeRead, Me } from '../api/endpoints.js';
import { readFlags, resumeStep } from './machine.js';

/**
 * The two dashboard action cards `GET /v1/recommendations`
 * (`src/recommendations/catalog.ts`) does not and cannot provide.
 * `screens/dashboard.tsx` renders these ahead of the server's own list,
 * which it now renders verbatim (title/description/urgent/target) the same
 * way `screens/protection.tsx` already did — see that file for the rest of
 * the wiring, including the `EmailVerification` special case.
 *
 * This file used to generate the WHOLE eight-card list client-side
 * (verify_email, upload_photos, quiz_refresh, review_score, complete_profile,
 * take_quiz, on top of these two) — a second, independent implementation of
 * exactly what the business catalog computes, disagreeing with it in two
 * reachable ways: `review_score` re-derived the low/moderate/high split from
 * `live < 80` on the client (the catalog gates on the server's own `band`,
 * spec §6 item 4 — never a client-side number threshold, the same rule the
 * score band itself follows), and `take_quiz` fired on `live === null` where
 * the server fires on "no current-version quiz response", so a swallowed
 * submit-time recompute failure (score: null, response stored) told someone
 * who had just finished the quiz to take it again. Both are gone along with
 * every other duplicate; only the two cards below have no server equivalent.
 *
 *  - `keep_plan`: a subscription about to lapse, from `HomeRead.subscription`
 *    — billing state the recommendations catalog has no view of.
 *  - `finish_setup`: the ONBOARDING WIZARD's own resume point
 *    (`flow/machine.ts#resumeStep`), which walks steps (plan, consent,
 *    enrolment, notifications) the catalog was never asked to model — it is
 *    a "coverage isn't on yet" gate, not a maintenance action for someone
 *    already onboarded.
 *
 * NEITHER TAKES A SCORE INPUT — deliberately: `localActionsFor`'s signature
 * has no `live` or `band` parameter at all, so there is no surface here for
 * a risk word to be re-derived from a number. `actions.test.ts` pins the
 * arity so a future change cannot quietly reintroduce one.
 */

export interface RecommendedAction {
  id: string;
  title: string;
  description: string;
  urgent: boolean;
  href?: string;
  run?: () => void;
  running?: boolean;
}

export function localActionsFor(me: Me, subscription: HomeRead['subscription']): RecommendedAction[] {
  const actions: RecommendedAction[] = [];

  // First, above everything else: coverage that is about to end is the one
  // thing a person cannot fix later.
  if (subscription !== null && subscription.cancel_at !== null) {
    actions.push({
      id: 'keep_plan',
      title: 'Keep your plan',
      description: `Your plan is set to end on ${new Date(subscription.cancel_at).toLocaleDateString()}. Resume it to stay monitored.`,
      urgent: true,
      href: '#/plans',
    });
  }

  // The wizard's own resume logic, not `next_step !== null`: an unverified
  // email pins next_step at 'email' forever, and verification is a listed
  // action on the server catalog now, never a reason to send someone back
  // through onboarding.
  const hasExtras = me.person.employer_name !== null || me.person.school_name !== null;
  if (resumeStep(me.onboarding, hasExtras, readFlags()) !== 'done') {
    actions.push({
      id: 'finish_setup',
      title: 'Finish setting up your protection',
      description: 'Pick up onboarding where you left off — coverage stays off until it is done.',
      urgent: true,
      href: '#/setup',
    });
  }
  return actions;
}
